/*
 * Copyright Higharc Inc. 2019
 */
import { Vector2 } from "three";

export const BitmapUtil = {
  nearestSampleIndex: function (bitmapWidth, bitmapHeight, uv) {
    const intX = Math.max(Math.min(Math.floor(uv.x * bitmapWidth), bitmapWidth - 1), 0);
    const intY = Math.max(Math.min(Math.floor(uv.y * bitmapHeight), bitmapHeight - 1), 0);
    return intY * bitmapWidth + intX;
  },

  linearFilteredSample: function (bitmap, bitmapWidth, bitmapHeight, numChannels, uv, outputSample) {
    const pixelX = uv.x * bitmapWidth;
    let leftPixelIntX = Math.floor(pixelX - 0.5);
    const rightPixelIntX = Math.min(leftPixelIntX + 1, bitmapWidth - 1);
    leftPixelIntX = Math.max(leftPixelIntX, 0);
    const xT = Math.max(pixelX - (leftPixelIntX + 0.5), 0);
    const pixelY = uv.y * bitmapHeight;
    let lowPixelIntY = Math.floor(pixelY - 0.5);
    const highPixelIntY = Math.min(lowPixelIntY + 1, bitmapHeight - 1);
    lowPixelIntY = Math.max(lowPixelIntY, 0);
    const yT = Math.max(pixelY - (lowPixelIntY + 0.5), 0);
    for (let i = 0; i < numChannels; ++i) {
      const leftLowPixelChannel = bitmap[(lowPixelIntY * bitmapWidth + leftPixelIntX) * numChannels + i];
      const rightLowPixelChannel = bitmap[(lowPixelIntY * bitmapWidth + rightPixelIntX) * numChannels + i];
      const leftHighPixelChannel = bitmap[(highPixelIntY * bitmapWidth + leftPixelIntX) * numChannels + i];
      const rightHighPixelChannel = bitmap[(highPixelIntY * bitmapWidth + rightPixelIntX) * numChannels + i];
      outputSample[i] =
        leftLowPixelChannel * (1.0 - xT) * (1.0 - yT) +
        rightLowPixelChannel * xT * (1.0 - yT) +
        leftHighPixelChannel * (1.0 - xT) * yT +
        rightHighPixelChannel * xT * yT;
    }
  },

  linearDownsampleBitmap: function (bitmap, originalWidth, originalHeight, targetWidth, targetHeight, numChannels) {
    if (originalWidth === originalHeight && targetWidth === targetHeight && targetWidth * 2 === originalWidth) {
      return BitmapUtil.linearDownsampleSquareBitmap(bitmap, originalWidth, numChannels);
    }

    const downsampled = new bitmap.constructor(targetWidth * targetHeight * numChannels);
    const linearFilteredSample = new bitmap.constructor(numChannels);
    const uv = new Vector2();
    for (let intY = 0; intY < targetHeight; ++intY) {
      uv.y = (intY + 0.5) / targetHeight;
      for (let intX = 0; intX < targetWidth; ++intX) {
        uv.x = (intX + 0.5) / targetWidth;
        const dstIndex = (intY * targetWidth + intX) * numChannels;
        BitmapUtil.linearFilteredSample(bitmap, originalWidth, originalHeight, numChannels, uv, linearFilteredSample);
        for (let i = 0; i < numChannels; ++i) {
          downsampled[dstIndex + i] = linearFilteredSample[i];
        }
      }
    }
    return downsampled;
  },

  linearDownsampleSquareBitmap: function (bitmap, originalResolution, numChannels) {
    const downsampled = new bitmap.constructor(((originalResolution * originalResolution) / 4) * numChannels);
    for (let intY = 0; intY < originalResolution / 2; ++intY) {
      for (let intX = 0; intX < originalResolution / 2; ++intX) {
        const srcIndex0 = intY * 2 * originalResolution + intX * 2;
        const srcIndex1 = srcIndex0 + 1;
        const srcIndex2 = srcIndex0 + originalResolution;
        const srcIndex3 = srcIndex2 + 1;
        const dstIndex = (intY * originalResolution) / 2 + intX;
        for (let i = 0; i < numChannels; ++i) {
          downsampled[dstIndex * numChannels + i] =
            (bitmap[srcIndex0 * numChannels + i] +
              bitmap[srcIndex1 * numChannels + i] +
              bitmap[srcIndex2 * numChannels + i] +
              bitmap[srcIndex3 * numChannels + i]) /
            4;
        }
      }
    }
    return downsampled;
  },
};
